/**
 * The hook the three connection lists share (FTP/SFTP sidebar, DB Manager,
 * Terminal host picker — cards 5.2–5.4): the project's connections on top,
 * the rest below (collapsible, remembered per list), the row-menu actions for
 * the ACTIVE project, and "a connection saved while a project is active joins
 * it" (owner decision 0.2).
 *
 * The vault modal saves on its own and does not hand back the new id, so the
 * caller says `expectNew()` when it opens a CREATE; the first new row that
 * appears afterwards is added to the active project.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProjectFilterScope } from './projectMembership';
import { addItemToProject, removeItemFromProject } from './projectItems';
import { canJoinProject, connectionNodeId, connectionWireScope, groupConnections, newIds } from './connectionProjectGroups';

const collapsedKey = (listId) => `apiboo.projectScope.othersCollapsed:${listId}`;
const warn = (err) => console.warn('[project-scope] connection membership failed:', err);

export default function useConnectionProjectGroups(rows, { kind, listId }) {
  const scope = useProjectFilterScope();
  const groups = useMemo(() => groupConnections(rows, scope, kind), [rows, scope, kind]);

  const [othersCollapsed, setOthersCollapsed] = useState(() => {
    try {
      return localStorage.getItem(collapsedKey(listId)) === '1';
    } catch {
      return false;
    }
  });
  const toggleOthers = useCallback(() => {
    setOthersCollapsed((prev) => {
      try {
        localStorage.setItem(collapsedKey(listId), prev ? '0' : '1');
      } catch {
        /* storage unavailable: still toggles for this session */
      }
      return !prev;
    });
  }, [listId]);

  const active = scope.status === 'ready' ? scope.activeProject : null;

  /** `{ kind: 'add' | 'remove', project, run }` for a row under the active project, or null. */
  const membershipAction = useCallback(
    (row) => {
      if (!active || !canJoinProject(row)) return null;
      const node = connectionNodeId(kind, row);
      if (scope.memberIds.has(node)) return { kind: 'remove', project: active, run: () => removeItemFromProject(active, node).catch(warn) };
      return { kind: 'add', project: active, run: () => addItemToProject(active, node, connectionWireScope(row)).catch(warn) };
    },
    [active, kind, scope.memberIds]
  );

  // New connection → active project.
  const pendingRef = useRef(null); // ids before the create, or null
  const expectNew = useCallback(() => {
    pendingRef.current = (rows || []).map((r) => r.id);
  }, [rows]);
  useEffect(() => {
    const before = pendingRef.current;
    if (!before || !active) return;
    const added = newIds(before, (rows || []).map((r) => r.id));
    if (!added.length) return;
    pendingRef.current = null;
    const row = rows.find((r) => String(r.id) === added[0]);
    if (canJoinProject(row)) addItemToProject(active, connectionNodeId(kind, row), connectionWireScope(row)).catch(warn);
  }, [rows, active, kind]);

  return { ...groups, othersCollapsed, toggleOthers, membershipAction, expectNew, projectName: active?.name || null };
}
